import { Tabs, Tab, Box } from '@mui/material';
import React, { useState } from 'react';
import { TabsComponentProps, TabVariant } from '@utils/tabs.utils.ts';
import { useTranslation } from 'react-i18next';
import {
  CloseIcon,
  EditBlackIcon
} from '@shared/components/icons/svg-icon.tsx';

const segmentedContainerSx = {
  width: '100%',
  backgroundColor: 'var(--gray-11)',
  border: '1px solid var(--gray-7)',
  borderRadius: '16px',
  padding: '4px',
  boxShadow: '0px 1px 2px 0px var(--black-alpha-50)',
  display: 'inline-flex',
  alignItems: 'center',
  maxHeight: '46px'
};

const segmentedTabsSx = {
  minHeight: 'unset',
  width: '100%',
  maxWidth: '100%',
  '& .MuiTabs-indicator': {
    height: '100%',
    borderRadius: '12px',
    backgroundColor: 'var(--white)',
    boxShadow: '0px 1px 3px 0px var(--black-alpha-100)',
    zIndex: 0
  },
  '& .MuiTabs-flexContainer': { gap: 0 }
};

const segmentedTabItemSx = {
  flex: 1,
  maxWidth: '100%',
  minHeight: '36px !important',
  height: '36px !important',
  minWidth: 'unset',
  borderRadius: '12px',
  padding: '4px 14px !important',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--gray-2)',
  textTransform: 'none',
  flexDirection: 'row',
  gap: '8px',
  zIndex: 1,
  transition: 'color 0.25s ease',
  '& .MuiTab-iconWrapper': {
    marginBottom: 0
  },
  '&.Mui-selected': {
    color: 'var(--primary)'
  }
};

const TabsComponent: React.FC<TabsComponentProps> = ({
  value,
  onChange,
  tabs,
  variant = TabVariant.Default,
  segmentedCompact = false,
  disabled = {},
  onDelete,
  onEdit
}) => {
  const { t } = useTranslation();
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const isSegmented = variant === TabVariant.Segmented;
  const segmentedSingleTab = isSegmented && tabs.length === 1;

  const handleDeleteClick = (e: React.MouseEvent, tab: any) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(tab);
    }
  };

  const handleEditClick = (e: React.MouseEvent, tab: any) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(tab);
    }
  };

  const renderDefaultLabel = (tab: (typeof tabs)[number]) => (
    <div className="flex items-center gap-2">
      <span>{t(tab.label)}</span>
      <div className="flex items-center gap-1">
        {tab.showEditIcon && (
          <div
            className="flex items-center justify-center"
            style={{ width: '20px', height: '20px' }}
          >
            {onEdit && hoveredTab === tab.value && (
              <div
                onClick={(e) => handleEditClick(e, tab)}
                className="cursor-pointer p-1 rounded hover:bg-gray-100 transition-colors"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%'
                }}
              >
                <EditBlackIcon style={{ width: '13px', height: '13px' }} />
              </div>
            )}
          </div>
        )}
        {tab.showDeleteIcon && (
          <div
            className="flex items-center justify-center"
            style={{ width: '20px', height: '20px' }}
          >
            {onDelete && hoveredTab === tab.value && (
              <div
                onClick={(e) => handleDeleteClick(e, tab)}
                className="cursor-pointer p-1 rounded hover:bg-gray-100 transition-colors"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%'
                }}
              >
                <CloseIcon style={{ width: '10px', height: '10px' }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const getTabIcon = (
    tab: (typeof tabs)[number]
  ): string | React.ReactElement | undefined => {
    if (isSegmented) {
      const icon = tab.icon as React.ReactElement;
      if (!icon) return undefined;
      return tab.value === value
        ? React.cloneElement(icon as React.ReactElement<{ stroke?: string }>, {
          stroke: 'var(--primary)'
        })
        : icon;
    }
    return tab?.flag ? (
      <img
        className="max-h-5 h-5 w-5 mr-2 rounded-4xl object-cover"
        src={tab.flag}
        alt="Flag"
      />
    ) : (
      ''
    );
  };

  return (
    <Box
      sx={
        isSegmented
          ? {
            ...segmentedContainerSx,
            ...(segmentedSingleTab
              ? { width: 'fit-content', maxWidth: '100%', alignSelf: 'flex-start' }
              : {})
          }
          : { display: 'inline-flex', position: 'relative' }
      }
    >
      <Tabs
        value={value}
        onChange={onChange}
        aria-label="navigation tabs"
        sx={
          isSegmented
            ? {
              ...segmentedTabsSx,
              ...(segmentedSingleTab
                ? { width: 'auto', minWidth: 0, flex: '0 0 auto' }
                : {})
            }
            : undefined
        }
      >
        {tabs.map((tab) => (
          <Tab
            disableRipple
            disabled={disabled[tab.value] || false}
            value={tab.value}
            data-tour={tab.dataTour}
            iconPosition={isSegmented ? 'start' : 'top'}
            onMouseEnter={
              !isSegmented ? () => setHoveredTab(tab.value) : undefined
            }
            onMouseLeave={!isSegmented ? () => setHoveredTab(null) : undefined}
            icon={getTabIcon(tab)}
            label={isSegmented ? t(tab.label) : renderDefaultLabel(tab)}
            key={tab.value}
            sx={
              isSegmented
                ? {
                  ...segmentedTabItemSx,
                  ...(segmentedSingleTab
                    ? { flex: '0 0 auto', maxWidth: 'none' }
                    : {}),
                  ...(segmentedCompact
                    ? {
                      flex: '1 1 0% !important',
                      minWidth: '0 !important',
                      maxWidth: 'none',
                      padding: '4px 4px !important',
                      fontSize: '12px',
                      gap: '4px',
                      overflow: 'hidden',
                      '& .MuiTab-iconWrapper': {
                        marginRight: '2px',
                        flexShrink: 0
                      },
                      '& .MuiTab-wrapper': {
                        minWidth: 0,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        overflow: 'hidden'
                      },
                      '& .MuiTab-wrapper > *:last-child': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }
                    }
                    : {})
                }
                : { textTransform: 'none' }
            }
          />
        ))}
      </Tabs>
      {!isSegmented && (
        <Box
          sx={{
            position: 'absolute !important',
            bottom: '1.5px !important',
            zIndex: 1,
            height: '1px',
            width: '100%',
            backgroundColor: disabled ? 'var(--gray-8)' : 'var(--blue-12)',
            '@media (max-width: 1715px)': {
              bottom: '1px !important'
            }
          }}
        />
      )}
    </Box>
  );
};

export default TabsComponent;
